const ORDER_SCREENS: Record<string, string> = {
  overview: 'Overview',
  'closing-disclosure': 'Closing Disclosure',
  'dates-and-config': 'Dates and Config',
  properties: 'Properties',
  parties: 'Parties',
  'earnest-and-commissions': 'Earnest and Commissions',
  payoffs: 'Mortgage Payoffs',
  documents: 'Documents',
  loans: 'Loans',
  'taxes-and-prorations': 'Taxes and Prorations',
  narrative: 'Narrative',
  'policy-info-and-rates': 'Policy Info and Rates',
  charges: 'Charges',
  'projected-payments': 'Projected Payments',
  'cash-to-close': 'Cash to Close',
  revenue: 'Revenue',
  'payments/wire-transfers': 'Payments · Wire Transfers',
  'payments/disbursements-and-receipts': 'Payments · Disbursements and Receipts',
  'payments/accounting': 'Payments · Accounting',
  'payments/settlement': 'Payments · Settlement',
  notifications: 'Notifications',
  notes: 'Notes',
  'closing/schedule-closing': 'Closing · Schedule Closing',
  'ledger-test': 'Ledger Test',
};

const APP_SCREENS: Record<string, string> = {
  contacts: 'Contacts',
  'document-templates': 'Document Templates',
  'email-templates': 'Email Templates',
  roles: 'Roles',
  orders: 'Orders',
  'terms-of-service': 'Terms of Service',
  'charge-settings': 'Charge Settings',
  'ai-config': 'AI Config',
  inference: 'Assistant',
  imports: 'Imports',
};

const ID_PATTERNS = [
  /^[0-9a-f]{24}$/i,
  /^\d+$/,
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
];

const isIdLike = (segment: string): boolean => ID_PATTERNS.some((pattern) => pattern.test(segment));

// A malformed `%` must not throw, and a decoded `%0A` must not be able to start a new header line.
const safeDecode = (segment: string): string => {
  try {
    return decodeURIComponent(segment).replace(/\s+/g, ' ').trim();
  } catch {
    return segment.replace(/\s+/g, ' ').trim();
  }
};

const titleCase = (segment: string): string => safeDecode(segment)
  .split(/[-_]/)
  .filter(Boolean)
  .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
  .join(' ');

// Longest known prefix of the order sub-route wins, so `loans/<loanId>` and `parties/<partyId>` resolve.
const orderScreenLabel = (segments: string[]): string => {
  const known = segments
    .map((_, index) => segments.slice(0, segments.length - index).join('/'))
    .find((key) => ORDER_SCREENS[key] !== undefined);
  if (known) return ORDER_SCREENS[known];
  if (segments.length === 0) return 'Order';
  return titleCase(segments[0]);
};

/** Human-readable name of the screen at `pathname`; id-like segments never become the label. */
export const deriveScreenLabel = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'Home';

  if (segments[0] === 'orders' && segments[1] === 'details') {
    return orderScreenLabel(segments.slice(3).filter((segment) => !isIdLike(segment)));
  }

  const meaningful = segments.filter((segment) => !isIdLike(segment));
  if (meaningful.length === 0) return 'Home';
  return APP_SCREENS[meaningful[0]] ?? titleCase(meaningful[meaningful.length - 1]);
};
